import { Modal, type ModalProps } from '@nespresso-design-system/components';
import { useSignal } from '@preact/signals';
import type { JSX, ComponentChildren } from 'preact';
import { useEffect, useId } from 'preact/hooks';

type SizeType = 'small' | 'medium' | 'large';

export type ModalOnDemandProps = {
  closeBtnA11yLabel: string;
  isOpen: boolean;
  heading?: string;
  onClose: () => void;
  onTouchStart?: (event: TouchEvent) => void;
  onTouchEnd?: (event: TouchEvent) => void;
  children?: ComponentChildren;
  fixedFooter?: boolean;
  footerSlot?: JSX.Element;
  position?: ModalProps['position'];
  isMediaModal?: boolean;
  size?: SizeType;
  class?: string;
} & Omit<JSX.HTMLAttributes<HTMLDialogElement>, 'size'>;

// TODO: Move to containers
export default function ModalOnDemand({
  closeBtnA11yLabel,
  isOpen,
  heading,
  position,
  isMediaModal,
  size,
  onTouchStart,
  onTouchEnd,
  onClose,
  children,
  fixedFooter,
  footerSlot,
  class: className,
  ...dialogAttributes
}: ModalOnDemandProps) {
  const isVisible = useSignal<boolean>(isOpen);
  const id = useId();

  // Note: Do a re-render to start the open/close transition. It's necessary
  // to have a delay between the modal being mounted and the transition
  // starting, otherwise the transition won't be visible.
  useEffect(() => {
    const delayMS = isOpen ? 0 : 300;
    const timeout = setTimeout(() => {
      isVisible.value = isOpen;
    }, delayMS);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  // Avoid unnecessary DOM nodes when the modal is closed
  if (!isOpen && !isVisible.value) return null;

  return (
    <Modal
      {...dialogAttributes}
      closeBtnA11yLabel={closeBtnA11yLabel}
      id={id}
      contentSlot={children}
      heading={heading}
      fixedFooter={fixedFooter}
      footerSlot={footerSlot}
      className={className}
      onClose={onClose}
      position={position}
      // @ts-ignore (DS excluded the type for this release but it still exist)
      isMediaModal={isMediaModal}
      // Note: there is a type mismatch here between DS & Preact
      size={size as ModalProps['size']}
      isOpen={isVisible.value}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    />
  );
}
